import './imageblock.less'

import {m, Picture} from '@codeurs/front'
import {FunctionComponent, useRef, useState} from 'react'
import {useMediaQuery} from 'react-responsive'
import {ImageType, LinkType} from 'types'
import {isIE11} from 'util/isie'
import {PageLink} from 'util/pagelink'
import {style} from 'util/style'
import {ZoomImg} from 'util/zoomimg'
import {ImageLoader} from 'views/partials/imageloader'
import {Theme} from 'views/theme'
import {Block} from './blocks'

export type FloatProp = 'left' | 'right' | 'none' | 'inherit'
export type ImageBlockType = {
	image: ImageType
	image_title?: string
	image_link?: LinkType
	image_description?: string
	image_width?: string
	image_height?: string
	image_center?: boolean
	image_float?: FloatProp
	imagetextblock?: boolean
}

const styles = style('imageblock', ['link', 'image', 'popup'])
export const ImageBlock: FunctionComponent<ImageBlockType> = props => {
	const {image, image_description, image_title} = props
	if (!image || !image.src) return null
	return (
		<Block class={styles.mod({center: props.image_center})}>
			{image_title && <div class={styles('title')}>{image_title}</div>}
			<ImageUseLink {...props} />
			{image_description && (
				<div class={styles('description')}>
					<Theme.Wysiwyg>{image_description}</Theme.Wysiwyg>
				</div>
			)}
		</Block>
	)
}

export const ImageUseLink: FunctionComponent<ImageBlockType> = ({
	children,
	image_link,
	imagetextblock = false,
	...image
}) => {
	const width = image.image_width ? image.image_width : image.image.width
	if (image_link)
		return (
			<div
				style={{
					minWidth: imagetextblock ? width + 'px' : 'auto',
					margin: image.image_center ? '0 auto' : '0'
				}}
			>
				<PageLink to={image_link.url} class={styles.link()}>
					<ImageLoader alt={image.image.alt} {...image} />
				</PageLink>
			</div>
		)
	return <ImagePopup imagetextblock={imagetextblock} {...image} />
}

export const ImagePopup: FunctionComponent<ImageBlockType> = ({
	image,
	image_description: text,
	image_width,
	image_height,
	image_center,
	image_float,
	imagetextblock
}) => {
	const {class: className} = image
	const [zoomed, setZoomed] = useState(false)
	const toggleRef = useRef<any>()
	const isMobile = useMediaQuery({query: '(max-width: 1200px)'})

	// images are default centered
	// dirty cms fix
	if (typeof image_center === 'undefined') image_center = true

	const imageStyling = {
		float: image_float,
		marginLeft: image_float === 'right' && 20,
		marginRight: image_float === 'left' && 20
	}

	return (
		<div
			class={
				styles.popup.mod({center: image_center}) +
				(className ? ' ' + className : '')
			}
			style={imageStyling}
		>
			{isIE11 || isMobile ? (
				<ZoomImg
					size="m"
					{...image}
					style={{
						width: image_width
							? image_width + 'px'
							: image.width
							? image.width
							: null,
						margin: '0 auto'
					}}
					imagetextblock={imagetextblock}
					customwidth={image_width}
					customheight={image_height}
				/>
			) : (
				<ZoomImg
					zoomed={zoomed}
					setZoomed={checkZoomed => {
						if (toggleRef.current) clearTimeout(toggleRef.current)
						if (checkZoomed) {
							toggleRef.current = setTimeout(() => setZoomed(checkZoomed))
						} else {
							setZoomed(checkZoomed)
						}
					}}
					{...image}
					size="m"
					imagetextblock={imagetextblock}
					customwidth={image_width}
					customheight={image_height}
				/>
			)}
		</div>
	)
}
